'use client';

import clsx from 'clsx';
import { ArrowUp, LoaderCircle, Mic } from 'lucide-react';
import {
	type InputHTMLAttributes,
	type KeyboardEvent,
	type RefObject,
	useId,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { ChatCommandMenu } from './command-menu';
import type { ChatCommand } from './commands';
import { chatStyles } from './styles';
import { type CommandMenuMode, useCommandMenu } from './use-command-menu';

export type ChatComposerVoice = {
	disabled?: boolean;
	recording?: boolean;
	levels?: number[];
	onToggle: () => void;
};

export type ChatComposerProps = {
	value: string;
	onChange: (value: string) => void;
	onSend: () => void;
	disabled?: boolean;
	sendDisabled?: boolean;
	loading?: boolean;
	sendOnEnter?: boolean;
	sendLabel?: string;
	placeholder?: string;
	disabledPlaceholder?: string;
	ariaLabel?: string;
	invalid?: boolean;
	error?: string;
	maxLength?: number;
	fieldRef?: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
	inputProps?: Pick<
		InputHTMLAttributes<HTMLInputElement>,
		| 'id'
		| 'type'
		| 'autoComplete'
		| 'inputMode'
		| 'pattern'
		| 'maxLength'
		| 'required'
		| 'aria-label'
		| 'onInvalid'
	>;
	commands?: readonly ChatCommand[];
	onCommand?: (command: ChatCommand, source: CommandMenuMode) => void;
	onMenuOpenChange?: (open: boolean, source: CommandMenuMode) => void;
	onEngage?: () => void;
	voice?: ChatComposerVoice;
};

const noCommands: readonly ChatCommand[] = [];

/** One visual implementation for every dialogue. Callers configure behavior only. */
export function ChatComposer({
	value,
	onChange,
	onSend,
	disabled,
	sendDisabled,
	loading = false,
	sendOnEnter = true,
	sendLabel = 'Отправить',
	placeholder,
	disabledPlaceholder,
	ariaLabel = 'Твой ответ',
	invalid,
	error,
	maxLength,
	fieldRef,
	inputProps,
	commands = noCommands,
	onCommand,
	onMenuOpenChange,
	onEngage,
	voice,
}: ChatComposerProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
	const engaged = useRef(false);
	const errorId = useId();
	const [inputHeight, setInputHeight] = useState(48);
	const blocked = disabled || loading;
	const hasCommands = !inputProps && commands.length > 0;
	const menu = useCommandMenu({
		commands: hasCommands ? commands : noCommands,
		value,
		onChange,
		inputRef,
		onExecute: onCommand,
		onOpenChange: onMenuOpenChange,
	});

	useLayoutEffect(() => {
		const field = inputRef.current;
		if (!field || inputProps) return;
		const resize = () => {
			field.style.height = '48px';
			const height = Math.max(48, Math.min(144, field.scrollHeight));
			field.style.height = `${height}px`;
			setInputHeight(height);
		};
		resize();
		let width = field.getBoundingClientRect().width;
		const observer = new ResizeObserver(([entry]) => {
			if (entry.contentRect.width !== width) {
				width = entry.contentRect.width;
				resize();
			}
		});
		observer.observe(field);
		return () => observer.disconnect();
	}, [value, inputProps]);

	const commandSelected = hasCommands && menu.isCommand;
	const cannotSend =
		blocked ||
		!value.trim() ||
		(commandSelected ? menu.items[menu.selected]?.disabled : sendDisabled);
	const send = () => {
		if (cannotSend) return;
		if (commandSelected) {
			if (menu.open && menu.mode === 'slash')
				void menu.select(menu.items[menu.selected]);
			else menu.changeText(value);
		} else onSend();
	};
	const onKeyDown = (
		event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		if (blocked || event.nativeEvent.isComposing || event.keyCode === 229)
			return;
		if (hasCommands && menu.keyDown(event)) return;
		if (sendOnEnter && event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			send();
		}
	};
	const change = (text: string) => {
		if (hasCommands) menu.changeText(text);
		else onChange(text);
		if (!engaged.current) {
			engaged.current = true;
			try {
				onEngage?.();
			} catch {
				/* Analytics cannot block input. */
			}
		}
	};
	const fieldProps = {
		ref: (node: HTMLInputElement | HTMLTextAreaElement | null) => {
			inputRef.current = node;
			if (fieldRef) fieldRef.current = node;
		},
		value,
		onKeyDown,
		onChange: (
			event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		) => change(event.target.value),
		disabled: blocked,
		placeholder:
			placeholder ?? (blocked ? disabledPlaceholder : 'Напиши ответ…'),
		'aria-label': inputProps?.['aria-label'] ?? ariaLabel,
		'aria-invalid': invalid || !!error || undefined,
		'aria-describedby': error ? errorId : undefined,
		className: chatStyles.field,
	};
	const voiceDisabled = blocked || !!inputProps || voice?.disabled;

	return (
		<div>
			<div
				ref={containerRef}
				data-chat-composer
				className={chatStyles.composer}
			>
				<div className="min-h-12">
					{inputProps ? (
						<input {...inputProps} {...fieldProps} />
					) : (
						<textarea
							{...fieldProps}
							rows={2}
							maxLength={maxLength}
							data-dialog-input={hasCommands || undefined}
							aria-controls={hasCommands && menu.open ? menu.listId : undefined}
							aria-activedescendant={
								hasCommands && menu.open && menu.items.length
									? `${menu.listId}-${menu.selected}`
									: undefined
							}
						/>
					)}
				</div>
				<div className="mt-1 flex items-center gap-2">
					{hasCommands && (
						<ChatCommandMenu
							menu={menu}
							anchorRef={containerRef}
							sideOffset={inputHeight + 28}
						/>
					)}
					<div className="ml-auto flex items-center gap-2">
						{voice && (
							<button
								type="button"
								title={voice.recording ? 'Стоп' : 'Голосовой ввод'}
								aria-label={voice.recording ? 'Стоп' : 'Голосовой ввод'}
								aria-pressed={voice.recording || false}
								disabled={voiceDisabled}
								onClick={() => {
									if (!voiceDisabled) voice.onToggle();
								}}
								className={clsx(
									'flex size-8 items-center justify-center rounded-lg border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:text-gray-4',
									voice.recording
										? 'border-red bg-red-light text-red-hover'
										: 'border-transparent text-gray-3 hover:bg-gray-5',
								)}
							>
								{voice.recording ? (
									<span className="flex h-4 items-center gap-[2px]" aria-hidden>
										{(voice.levels ?? []).map((level, i) => (
											<span
												key={i}
												className="w-[2.5px] rounded-full bg-red-hover"
												style={{ height: `${3 + Math.min(9, level * 0.09)}px` }}
											/>
										))}
									</span>
								) : (
									<Mic size={16} strokeWidth={2} aria-hidden />
								)}
							</button>
						)}
						<button
							type="button"
							data-dialog-submit={hasCommands || undefined}
							aria-label={commandSelected ? 'Выполнить команду' : sendLabel}
							aria-busy={loading}
							onClick={send}
							disabled={cannotSend}
							className={clsx(
								chatStyles.sendButton,
								cannotSend ? chatStyles.sendEmpty : chatStyles.sendActive,
							)}
						>
							{loading ? (
								<LoaderCircle aria-hidden size={16} className="animate-spin" />
							) : (
								<ArrowUp aria-hidden size={16} strokeWidth={2.4} />
							)}
						</button>
					</div>
				</div>
			</div>
			{error && (
				<p id={errorId} role="alert" className="mt-2.5 text-sm text-red">
					{error}
				</p>
			)}
		</div>
	);
}
